/**
 * @fileoverview SkeletonTitle — title-line shimmer.
 * @package @puffin-core/skeleton-kit
 */

import { memo } from 'react';
import PropTypes from 'prop-types';

/**
 * @typedef {object} SkeletonTitleOwnProps
 * @property {string} [width] - A CSS length.
 * @property {boolean} [animated=true]
 * @property {string} [className]
 */

/**
 * The remaining props (an `aria-*` override, a test id) are spread onto the element.
 *
 * @typedef {SkeletonTitleOwnProps & React.HTMLAttributes<HTMLDivElement>} SkeletonTitleProps
 */

/**
 * Memoised; it keeps its `propTypes` declaration until JSDOC-PROP-CONTRACT-001 Phase 3 retires it.
 *
 * @type {React.NamedExoticComponent<SkeletonTitleProps> & {propTypes?: object}}
 */
export const SkeletonTitle = memo(
  /**
   * @param {SkeletonTitleProps} props
   * @returns {React.JSX.Element}
   */
  function SkeletonTitle({ width, animated = true, className = '', ...props }) {
    const style = width ? { width } : undefined;
    const classes = ['skeleton', 'skeleton-title', animated ? null : 'skeleton-static', className]
      .filter(Boolean)
      .join(' ')
      .trim();

    return <div className={classes} aria-hidden="true" style={style} {...props} />;
  },
);

SkeletonTitle.propTypes = {
  width: PropTypes.string,
  animated: PropTypes.bool,
  className: PropTypes.string,
};

export default SkeletonTitle;
